import type { ReactNode } from "react";
import { AlertTriangle } from "./icons";

/**
 * A qualification the reader must see **before** they read the figures (`003` R4).
 *
 * First consumer: `excludesReceipts` on the ageing report. `customers-admin.md` is blunt about the
 * consequence — an ageing report that excludes receipts "shows every customer who has paid as still
 * owing, which is not a slightly stale number, it is the wrong list of people to chase". Second
 * consumer, already known: `010`'s agent-down notice. Shared from first use for that reason.
 *
 * ## The qualified content is a child, not a sibling
 *
 * A banner "rendered above the content" is a convention, and conventions get reordered. Taking the
 * content as `children` makes the ordering structural: there is no way to compose this that puts the
 * caveat after the figures. Same idea as `PriceDisplay`'s signature — the wrong version is
 * unwritable, not discouraged.
 *
 * ## There is no dismiss control, and there will not be one
 *
 * A caveat that can be closed is a caveat that is closed for everyone after the first week, on the
 * screen where being clear matters more than being tidy. It is also not a caption and not a tooltip:
 * both are read after the number, or never.
 */
export function CaveatBanner({
  caveat,
  action,
  children,
}: {
  /** What is wrong with the figures below, in plain words. Not "note:". */
  caveat: ReactNode;
  /** Where to go about it — the agent health screen, the sync queue. Optional. */
  action?: ReactNode;
  /** The content the caveat qualifies. Rendered beneath it, always. */
  children?: ReactNode;
}) {
  return (
    <div data-slot="caveat-banner" className="flex flex-col gap-4">
      <div
        role="note"
        className="border-orange/45 bg-orange/10 text-foreground flex gap-3.5 rounded-xl border p-4 text-sm"
      >
        <span className="bg-orange/15 text-orange-dark mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg">
          <AlertTriangle aria-hidden className="size-4" />
        </span>

        <div className="flex min-w-0 flex-col gap-1.5">
          <p className="font-semibold">{caveat}</p>
          {action ? <div className="text-sm">{action}</div> : null}
        </div>
      </div>

      {children}
    </div>
  );
}
